import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Check, Clock, Building2, Phone, Mail, MapPin, Shield, Calendar, DollarSign, CreditCard, X, Landmark } from 'lucide-react';
import { useStax } from '@/hooks/useStax';

interface Proposal {
  id: string;
  slug: string;
  company_id: string;
  customer_name: string;
  customer_attention: string | null;
  customer_email: string;
  customer_phone: string | null;
  customer_address: string | null;
  customer_city: string | null;
  customer_state: string | null;
  customer_zip: string | null;
  plan_name: string;
  plan_code: string | null;
  systems_count: number;
  visits_per_year: number;
  total_annual_price_cents: number;
  payment_structure: string;
  payment_terms_description: string | null;
  first_payment_cents: number | null;
  second_payment_cents: number | null;
  second_payment_due_date: string | null;
  benefits: string[];
  terms_content: string | null;
  status: string;
  proposal_date: string;
  valid_until: string | null;
  accepted_at: string | null;
  declined_at: string | null;
  created_by: string | null;
  company?: {
    id: string;
    name: string;
    logo_url: string | null;
    primary_color: string | null;
    secondary_color: string | null;
    domain: string | null;
  };
}

const CCS_LOGO = '/brands/climate-logo.png';
const CCS_GREEN = '#059669';

function formatCents(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

type PaymentMethod = 'card' | 'check' | null;

export default function CommercialProposalPage() {
  const { slug } = useParams<{ slug: string }>();
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [checkConfirmed, setCheckConfirmed] = useState(false);

  // Signer info
  const [signerName, setSignerName] = useState('');
  const [signerTitle, setSignerTitle] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  // Card form state
  const [cardName, setCardName] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const { cardFormStatus, mountCardForm, tokenizePaymentMethod, resetCardForm } = useStax();

  useEffect(() => {
    if (!slug) return;
    const apiBase = import.meta.env.VITE_API_URL || '/api';
    fetch(`${apiBase}/proposals/${slug}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.error) {
          setError(data.error);
        } else if (data.proposal) {
          setProposal(data.proposal);
          if (data.proposal.status === 'accepted') setAccepted(true);
        } else {
          setError('Proposal not found');
        }
      })
      .catch(() => setError('Failed to load proposal'))
      .finally(() => setLoading(false));
  }, [slug]);

  // Mount card form when payment method is card
  useEffect(() => {
    if (paymentMethod === 'card' && cardFormStatus === 'idle') {
      mountCardForm('climate');
    }
  }, [paymentMethod, cardFormStatus, mountCardForm]);

  const openPaymentModal = () => {
    setShowPaymentModal(true);
    setPaymentMethod(null);
    setPaymentError(null);
    setCheckConfirmed(false);
  };

  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setPaymentMethod(null);
    setPaymentError(null);
    resetCardForm();
  };

  const handleAcceptWithCheck = async () => {
    if (!slug || !proposal || !signerName.trim()) return;
    setPaymentProcessing(true);
    setPaymentError(null);
    try {
      const apiBase = import.meta.env.VITE_API_URL || '/api';
      const res = await fetch(`${apiBase}/proposals/${slug}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payment_method: 'check',
          accepted_by: signerName.trim(),
          accepted_by_title: signerTitle.trim() || null,
          terms_accepted: true,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setAccepted(true);
        setProposal((prev) => (prev ? { ...prev, status: 'accepted' } : prev));
        closePaymentModal();
      } else {
        setPaymentError(data.error || 'Failed to accept proposal');
      }
    } catch {
      setPaymentError('Network error. Please try again.');
    } finally {
      setPaymentProcessing(false);
    }
  };

  const handleAcceptWithCard = async () => {
    if (!slug || !proposal || !formRef.current || !signerName.trim()) return;
    setPaymentProcessing(true);
    setPaymentError(null);

    try {
      // Parse expiry (MM/YY format)
      const [expMonth, expYear] = cardExpiry.split('/').map(s => s.trim());
      if (!expMonth || !expYear) throw new Error('Please enter expiry date as MM/YY');

      // Tokenize card with Stax
      const nameParts = signerName.trim().split(' ');
      const tokenResult = await tokenizePaymentMethod({
        form: formRef.current,
        extraDetails: {
          firstname: nameParts[0] || '',
          lastname: nameParts.slice(1).join(' ') || '',
          person_name: cardName || signerName.trim(),
          email: proposal.customer_email,
          phone: proposal.customer_phone || undefined,
          address_1: proposal.customer_address || undefined,
          address_city: proposal.customer_city || undefined,
          address_state: proposal.customer_state || undefined,
          address_zip: proposal.customer_zip || undefined,
        },
        brandKey: 'climate',
      });

      if (!tokenResult?.id) throw new Error('Failed to tokenize card. Please check your card details.');

      // Send to backend to process payment and accept proposal
      const apiBase = import.meta.env.VITE_API_URL || '/api';
      const res = await fetch(`${apiBase}/proposals/${slug}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payment_method: 'card',
          payment_method_id: tokenResult.id,
          stax_customer_id: tokenResult.customer_id,
          accepted_by: signerName.trim(),
          accepted_by_title: signerTitle.trim() || null,
          terms_accepted: true,
        }),
      });

      const data = await res.json();
      if (data.success) {
        setAccepted(true);
        setProposal((prev) => (prev ? { ...prev, status: 'accepted' } : prev));
        closePaymentModal();
      } else {
        setPaymentError(data.error || 'Payment failed. Please try again.');
      }
    } catch (err: any) {
      setPaymentError(err.message || 'Payment processing failed. Please try again.');
    } finally {
      setPaymentProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="max-w-md text-center">
          <h1 className="text-xl font-semibold text-gray-900">Unable to load proposal</h1>
          <p className="mt-2 text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!proposal) return null;

  const logoUrl = proposal.company?.logo_url || CCS_LOGO;
  const primaryColor = proposal.company?.primary_color || CCS_GREEN;
  const isExpired = proposal.valid_until && new Date(proposal.valid_until) < new Date();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <img src={logoUrl} alt="Logo" className="h-10 object-contain" />
          <span className="text-sm text-gray-500">Commercial Membership Proposal</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Status Badge */}
        {accepted && (
          <div className="mb-6 p-4 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center gap-3">
            <Check className="w-6 h-6 text-emerald-600" />
            <span className="font-medium text-emerald-800">Proposal Accepted</span>
          </div>
        )}
        {isExpired && !accepted && (
          <div className="mb-6 p-4 rounded-xl bg-amber-50 border border-amber-200 flex items-center gap-3">
            <Clock className="w-6 h-6 text-amber-600" />
            <span className="font-medium text-amber-800">This proposal has expired</span>
          </div>
        )}

        {/* Customer Info Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Building2 className="w-6 h-6 text-gray-400" />
            <h2 className="text-xl font-semibold text-gray-900">Prepared For</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4 text-gray-700">
            <div>
              <p className="font-semibold text-lg">{proposal.customer_name}</p>
              {proposal.customer_attention && <p className="text-gray-600">Attn: {proposal.customer_attention}</p>}
            </div>
            <div className="space-y-1">
              {proposal.customer_address && (
                <p className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  {proposal.customer_address}, {proposal.customer_city}, {proposal.customer_state} {proposal.customer_zip}
                </p>
              )}
              {proposal.customer_phone && (
                <p className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-400" />
                  {proposal.customer_phone}
                </p>
              )}
              <p className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-gray-400" />
                {proposal.customer_email}
              </p>
            </div>
          </div>
        </div>

        {/* Plan Summary */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-6 h-6" style={{ color: primaryColor }} />
            <h2 className="text-xl font-semibold text-gray-900">{proposal.plan_name}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-gray-50 rounded-xl">
              <p className="text-3xl font-bold" style={{ color: primaryColor }}>{proposal.systems_count}</p>
              <p className="text-gray-600">HVAC Systems</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-xl">
              <p className="text-3xl font-bold" style={{ color: primaryColor }}>{proposal.visits_per_year}</p>
              <p className="text-gray-600">Visits Per System/Year</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-xl">
              <p className="text-3xl font-bold" style={{ color: primaryColor }}>{proposal.systems_count * proposal.visits_per_year}</p>
              <p className="text-gray-600">Total Annual Visits</p>
            </div>
          </div>
        </div>

        {/* Benefits */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Membership Benefits</h2>
          <ul className="space-y-3">
            {(proposal.benefits || []).map((benefit, i) => (
              <li key={i} className="flex items-start gap-3">
                <Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: primaryColor }} />
                <span className="text-gray-700">{benefit}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Investment & Payment */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <DollarSign className="w-6 h-6" style={{ color: primaryColor }} />
            <h2 className="text-xl font-semibold text-gray-900">Investment</h2>
          </div>
          <div className="text-center py-6">
            <p className="text-4xl font-bold" style={{ color: primaryColor }}>{formatCents(proposal.total_annual_price_cents)}</p>
            <p className="text-gray-600">Annual Membership</p>
            <p className="text-sm text-gray-500 mt-1">({formatCents(Math.round(proposal.total_annual_price_cents / proposal.systems_count))} per system/year)</p>
          </div>
          {proposal.payment_structure === 'split_half' && proposal.first_payment_cents && (
            <div className="bg-emerald-50 rounded-xl p-4 mt-4 border border-emerald-100">
              <p className="font-semibold text-emerald-800 mb-2">Split Payment Option</p>
              <ul className="space-y-1 text-emerald-700">
                <li>• First Payment: {formatCents(proposal.first_payment_cents)} upon acceptance</li>
                {proposal.second_payment_cents && proposal.second_payment_due_date && (
                  <li>• Second Payment: {formatCents(proposal.second_payment_cents)} due {formatDate(proposal.second_payment_due_date)}</li>
                )}
              </ul>
            </div>
          )}
        </div>

        {/* Dates */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Calendar className="w-6 h-6 text-gray-400" />
            <h2 className="text-xl font-semibold text-gray-900">Proposal Details</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4 text-gray-700">
            <p><span className="font-medium">Proposal Date:</span> {formatDate(proposal.proposal_date)}</p>
            {proposal.valid_until && <p><span className="font-medium">Valid Until:</span> {formatDate(proposal.valid_until)}</p>}
            {proposal.created_by && <p><span className="font-medium">Prepared By:</span> {proposal.created_by}</p>}
          </div>
        </div>

        {/* Terms Link */}
        {!accepted && !isExpired && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Terms & Conditions</h2>
            <p className="text-gray-600 mb-4">Please review our Comfort Club membership terms before accepting this proposal.</p>
            <button
              onClick={() => setShowTermsModal(true)}
              className="text-emerald-600 hover:text-emerald-700 font-medium underline"
            >
              View Full Terms & Conditions
            </button>
          </div>
        )}

        {/* Accept Button */}
        {!accepted && !isExpired && (
          <div className="text-center py-8">
            <button
              onClick={openPaymentModal}
              className="inline-flex items-center gap-2 px-8 py-4 text-lg font-semibold text-white rounded-xl shadow-lg hover:shadow-xl transition-all"
              style={{ backgroundColor: primaryColor }}
            >
              Accept Proposal & Choose Payment
            </button>
            <p className="text-sm text-gray-500 mt-3">You'll be asked to provide your signature and select a payment method.</p>
          </div>
        )}

        {/* Footer */}
        <footer className="mt-12 pt-8 border-t border-gray-200 text-center text-gray-500 text-sm">
          <p>Questions? Call us at <a href="tel:561-842-1477" className="font-medium" style={{ color: primaryColor }}>561-842-1477</a></p>
          <p className="mt-1">Climate Control Services • Palm Beach County, FL</p>
        </footer>
      </main>

      {/* Terms Modal */}
      {showTermsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-semibold">Comfort Club Terms & Conditions</h2>
              <button onClick={() => setShowTermsModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto prose prose-sm max-w-none">
              <h3>CLIMATE CONTROL SERVICES — Comfort Club Membership Terms & Conditions</h3>
              <p><strong>Climate Control Services, Inc.</strong><br/>Effective Date: January 2026</p>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 my-4">
                <p className="font-semibold text-amber-800 mb-2">KEY PAYMENT & RENEWAL TERMS:</p>
                <ol className="list-decimal list-inside text-amber-700 space-y-1 text-sm">
                  <li>This is an ANNUAL MEMBERSHIP (12-month term), NOT a month-to-month plan.</li>
                  <li>If you choose monthly billing, you are paying the annual fee in 12 monthly installments.</li>
                  <li>Once ANY membership benefit is used (discount, diagnostic waiver, maintenance visit, etc.), the FULL annual fee is earned and due.</li>
                  <li>Memberships AUTOMATICALLY RENEW each year unless you cancel before the renewal deadline.</li>
                  <li>To cancel, provide written notice (email, mail, or online form) or cancel by the same method you used to enroll, as required by Florida law.</li>
                </ol>
              </div>

              <h4>1. Definitions</h4>
              <ul>
                <li><strong>"CCS," "we," "us," or "our"</strong> means Climate Control Services, Inc.</li>
                <li><strong>"Member," "you," or "your"</strong> means the person(s) who enroll in the Membership and whose name appears on the account.</li>
                <li><strong>"Membership"</strong> means your enrollment in one of CCS's Comfort Club levels: Standard, Premier, or Optimum.</li>
                <li><strong>"Membership Year"</strong> means the 12-month period beginning on your start date and each 12-month renewal thereafter.</li>
                <li><strong>"Covered Property"</strong> means the service address listed on your account.</li>
                <li><strong>"Covered System"</strong> means an eligible central HVAC system or other equipment expressly listed as covered under your selected plan level.</li>
                <li><strong>"Membership Benefits"</strong> include member-only pricing, discounts, waived or reduced diagnostic fees, included maintenance visits, covered-repair allowances, replacement credits, priority scheduling, and any other benefit made available only to Members.</li>
              </ul>

              <h4>2. Membership Levels & Benefits Summary</h4>
              <p>CCS offers three Comfort Club membership levels. Specific benefits vary by level. All covered equipment must be in good working order at contract start; systems not functioning properly at initiation are subject to denial or cancellation.</p>

              <div className="overflow-x-auto my-4">
                <table className="min-w-full text-xs border">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border px-2 py-1 text-left">Benefit</th>
                      <th className="border px-2 py-1">Standard</th>
                      <th className="border px-2 py-1">Premier</th>
                      <th className="border px-2 py-1">Optimum</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td className="border px-2 py-1">Annual Premier Tune-Up (per system)</td><td className="border px-2 py-1 text-center">1 or 2/year</td><td className="border px-2 py-1 text-center">1 or 2/year</td><td className="border px-2 py-1 text-center">1 or 2/year</td></tr>
                    <tr><td className="border px-2 py-1">HVAC Diagnostic Fee</td><td className="border px-2 py-1 text-center">$69</td><td className="border px-2 py-1 text-center">$0 FREE</td><td className="border px-2 py-1 text-center">$0 FREE</td></tr>
                    <tr><td className="border px-2 py-1">Plumbing Diagnostic Fee</td><td className="border px-2 py-1 text-center">$69</td><td className="border px-2 py-1 text-center">$0 FREE</td><td className="border px-2 py-1 text-center">$0 FREE</td></tr>
                    <tr><td className="border px-2 py-1">Appliance Diagnostic Fee</td><td className="border px-2 py-1 text-center">$69</td><td className="border px-2 py-1 text-center">$69</td><td className="border px-2 py-1 text-center">$0 FREE</td></tr>
                    <tr><td className="border px-2 py-1">Repair Discounts</td><td className="border px-2 py-1 text-center">20% off</td><td className="border px-2 py-1 text-center">20% off</td><td className="border px-2 py-1 text-center">20% HVAC/Plumbing</td></tr>
                    <tr><td className="border px-2 py-1">HVAC Major Component Allowance</td><td className="border px-2 py-1 text-center">—</td><td className="border px-2 py-1 text-center">Up to $1,500/yr</td><td className="border px-2 py-1 text-center">Up to $1,500/yr</td></tr>
                    <tr><td className="border px-2 py-1">Water Heater Repair Allowance</td><td className="border px-2 py-1 text-center">—</td><td className="border px-2 py-1 text-center">Up to $500/yr</td><td className="border px-2 py-1 text-center">Up to $500/yr</td></tr>
                    <tr><td className="border px-2 py-1">Minor Plumbing Repairs</td><td className="border px-2 py-1 text-center">—</td><td className="border px-2 py-1 text-center">Included</td><td className="border px-2 py-1 text-center">Included</td></tr>
                    <tr><td className="border px-2 py-1">Standard Appliance Repair</td><td className="border px-2 py-1 text-center">—</td><td className="border px-2 py-1 text-center">—</td><td className="border px-2 py-1 text-center">Included</td></tr>
                    <tr><td className="border px-2 py-1">Loyalty Credit Toward New HVAC</td><td className="border px-2 py-1 text-center">$100/yr (max $1,000)</td><td className="border px-2 py-1 text-center">$100/yr (max $1,000)</td><td className="border px-2 py-1 text-center">$100/yr (max $1,000)</td></tr>
                    <tr><td className="border px-2 py-1">Priority Scheduling</td><td className="border px-2 py-1 text-center">✓</td><td className="border px-2 py-1 text-center">✓</td><td className="border px-2 py-1 text-center">✓</td></tr>
                    <tr><td className="border px-2 py-1">No After-Hours/Weekend Fees</td><td className="border px-2 py-1 text-center">✓</td><td className="border px-2 py-1 text-center">✓</td><td className="border px-2 py-1 text-center">✓</td></tr>
                  </tbody>
                </table>
              </div>

              <h5>2.1 Standard Comfort Club</h5>
              <ul>
                <li>One or two annual Premier Preventive Maintenance & Tune-Up(s) per covered HVAC system (includes drain pan treatment tablet)</li>
                <li>Reduced HVAC diagnostic fee ($69 vs $99) and priority scheduling</li>
                <li>20% discount on eligible HVAC, plumbing, and appliance repairs (labor and standard parts)</li>
                <li>20% discount on duct cleanings, filters, UV lights, and indoor air quality products</li>
                <li>Gold Standard workmanship guarantee on repairs performed during active Membership</li>
                <li>Loyalty credit: $100 per paid Membership Year toward a qualifying CCS-installed HVAC replacement (max $1,000)</li>
                <li>No after-hours or weekend service fees</li>
              </ul>

              <h5>2.2 Premier Comfort Club</h5>
              <p>Includes all Standard Comfort Club benefits, plus:</p>
              <ul>
                <li><strong>No HVAC or plumbing diagnostic fee</strong> for covered service calls ($0 vs $99)</li>
                <li>Reduced appliance diagnostic fee ($69 vs $99)</li>
                <li><strong>Covered HVAC repair allowance:</strong> up to $1,500 per Membership Year, per covered system, for eligible major components; 20% off non-covered repairs and overages</li>
                <li><strong>Covered water heater repair allowance:</strong> up to $500 per Membership Year for one eligible standard electric tank water heater (≤50 gal)</li>
                <li><strong>Minor plumbing repairs included</strong> (leaky faucets, clogged sink drains, running toilets, loose/worn fixtures, minor pipe leaks, toilet flange repair, garbage disposal issues)</li>
                <li>Replacement credits on certain non-repairable items (up to $1,500 HVAC; up to $500 water heater)</li>
                <li>24/7/365 emergency HVAC service access (after-hours dispatch based on technician availability)</li>
              </ul>

              <h5>2.3 Optimum Comfort Club</h5>
              <p>Includes all Standard and Premier Comfort Club benefits, plus:</p>
              <ul>
                <li><strong>No diagnostic fees</strong> for HVAC, plumbing, AND eligible appliance service calls ($0 across the board)</li>
                <li><strong>Minor plumbing repairs included</strong> (faucet cartridges, aerators, toilet internals, simple interior clogs, minor accessible leaks)</li>
                <li>Drain line rooting/clearing included up to the first accessible vertical stack/riser</li>
                <li><strong>Standard appliance repair coverage:</strong> refrigerator up to $500/yr, oven/range up to $500/yr, dishwasher up to $300/yr, washer up to $500/yr, dryer up to $500/yr, built-in microwave up to $200/yr, garbage disposal ≤½ HP</li>
                <li>50% off labor on eligible premium appliance and premium plumbing fixture repairs not otherwise covered</li>
                <li><strong>Enhanced replacement credits</strong> on certain non-repairable items (up to $3,000 HVAC, up to $500 water heater, up to $500 refrigerator, up to $500 each washer/dryer, up to $300 dishwasher, up to $200 microwave, up to $500 oven/range/cooktop, up to $250 garbage disposal)</li>
              </ul>

              <h4>3. Pricing</h4>
              <p>All prices include one Premier tune-up per system per year. For two tune-ups annually, contact us at (561) 680-4467 or select the 2-visit option at enrollment.</p>

              <h5>3.1 Standard Comfort Club Pricing</h5>
              <ul>
                <li><strong>1 visit per system per year:</strong> $16.99/month first system + $12.99/month each additional system</li>
                <li><strong>2 visits per system per year:</strong> $33.98/month first system + $25.98/month each additional system</li>
              </ul>

              <h5>3.2 Premier Comfort Club Pricing</h5>
              <ul>
                <li><strong>1 visit per system per year:</strong> $39.99/month first system + $16.99/month each additional system</li>
                <li><strong>2 visits per system per year:</strong> $79.98/month first system + $33.98/month each additional system</li>
              </ul>

              <h5>3.3 Optimum Comfort Club Pricing</h5>
              <ul>
                <li><strong>1 visit per system per year:</strong> $64.99/month first system + $16.99/month each additional system</li>
                <li><strong>2 visits per system per year:</strong> $129.98/month first system + $33.98/month each additional system</li>
              </ul>

              <h4>4. Term, Billing & Payment</h4>

              <h5>4.1 12-Month Term</h5>
              <p>Your Membership is a 12-month service contract. It begins on the date CCS successfully processes your first payment (or the date stated on your enrollment confirmation) and continues for 12 months.</p>

              <h5>4.2 Annual Fee; Monthly Installments</h5>
              <p>The annual Membership fee for your selected plan level is disclosed at enrollment. If you elect monthly billing, you agree that monthly billing is only an installment payment plan for the annual fee — it is not a month-to-month membership.</p>

              <h5>4.3 Payment Authorization</h5>
              <p>You authorize CCS to charge the payment method you provide for Membership fees and any amounts due under these Terms, including any remaining installment balance due after early termination.</p>

              <h5>4.4 Account Must Be Current</h5>
              <p>Membership Benefits are available only while your account is in good standing. If a payment fails or your account is past due, CCS may suspend Benefits until your account is brought current.</p>

              <h5>4.5 Returned Payments; Collections</h5>
              <p>If a payment is reversed, declined, or charged back, you remain responsible for all amounts due. CCS may use lawful collection methods to recover unpaid balances, and you agree to reimburse CCS for reasonable costs of collection to the extent permitted by law.</p>

              <h4>5. Use of Membership Benefits</h4>

              <h5>5.1 Annual Commitment</h5>
              <p>The Membership is priced and administered as an annual agreement. Enrolling means you agree to the full annual Membership fee for the Membership Year, whether paid annually or in monthly installments.</p>

              <h5>5.2 Benefit Use Triggers Full Annual Fee</h5>
              <p>Once any Membership Benefit is used during a Membership Year, the full annual Membership fee for that Year is earned and due. Examples of "use" include:</p>
              <ul>
                <li>A member discount applied to any invoice (HVAC, plumbing, appliance, products, or accessories)</li>
                <li>A waived or reduced diagnostic fee applied to a service visit</li>
                <li>Completion of any included maintenance/tune-up visit</li>
                <li>Any covered-repair allowance or replacement credit applied to a repair or replacement</li>
                <li>Any after-hours/weekend fee waiver or member-only pricing applied to an invoice</li>
              </ul>

              <h4>6. Automatic Renewal & Florida Law Compliance</h4>

              <h5>6.1 Auto-Renewal</h5>
              <p>Unless you cancel renewal as provided below, your Membership will automatically renew for successive 12-month terms at CCS's then-current rates for your plan level and covered systems.</p>

              <h5>6.2 Required Renewal Reminder Notice</h5>
              <p>For Memberships with a 12-month term that automatically renew for more than one month, CCS will provide written or electronic notification of the automatic renewal provision no less than 30 days and no more than 60 days before the cancellation deadline for renewal, as required by Florida Statutes § 501.165.</p>

              <h5>6.3 How to Stop Auto-Renewal</h5>
              <p>To prevent renewal for the next Membership Year, CCS must receive your cancellation notice before the cancellation deadline. Unless otherwise stated on your enrollment confirmation, the cancellation deadline is 11:59 p.m. (local time at the Covered Property) on the 3rd business day before your renewal date.</p>

              <h5>6.4 Cancellation Method</h5>
              <p>CCS will allow you to cancel the service contract in the same manner, and by the same means, as you accepted it, as required by Florida Statutes § 501.165. In addition, CCS accepts written cancellations by email, mail, or online form. Phone-only cancellations are not accepted.</p>

              <h4>7. Rescission & Cancellation</h4>

              <h5>7.1 Three-Business-Day Right to Cancel</h5>
              <p>If your enrollment qualifies as a "home solicitation sale" under Florida law, you have the right to cancel until midnight of the third business day after you sign or accept the agreement. CCS will honor this right where applicable.</p>

              <h5>7.2 Early Termination After Rescission Period</h5>
              <p>You may request early termination of your Membership at any time after the rescission period. Early termination ends future Membership Benefits as of the effective date confirmed by CCS; however, you remain responsible for the full annual Membership fee for the then-current Membership Year, including any unpaid monthly installments, even if no Membership Benefits were used.</p>

              <h5>7.3 No Proration/Refunds</h5>
              <p>Except where required by law, Membership fees are non-refundable and will not be prorated.</p>

              <h4>8. General Exclusions</h4>
              <p>Unless expressly covered above, the Membership does not cover:</p>
              <ul>
                <li><strong>Pre-Existing Conditions:</strong> Any condition or problem that existed prior to the contract's effective date. CCS reserves the right to determine pre-existing conditions and deny coverage.</li>
                <li><strong>Improper Installation or Code Violations:</strong> Equipment not installed according to standard industry practice or that does not meet local building codes or manufacturer requirements.</li>
                <li><strong>Negligence, Misuse, or Abuse:</strong> Any damage or failure caused by abuse, misuse, using equipment for non-residential purposes, intentional damage, or tampering by unqualified persons.</li>
                <li><strong>External Causes & Acts of God:</strong> Fire, smoke, explosion, lightning strike, power surges, flood, storm damage, hail, theft, vandalism, rodent damage, and natural disasters.</li>
                <li><strong>Rust or Corrosion:</strong> Damage or failure due to rust, corrosion, or sediment build-up, including salt-air corrosion.</li>
                <li><strong>Obsolete or Unavailable Parts:</strong> Parts no longer manufactured or reasonably available. CCS is not obligated to source parts from secondary markets.</li>
                <li><strong>Lines, Pipes, Wiring, and Ductwork:</strong> Any piping, wiring, or ductwork outside the immediate appliance or equipment, including those running through walls, ceilings, floors, or underground.</li>
                <li><strong>Refrigerant:</strong> Refrigerant is not covered and is billed at current market rates. CCS will not recharge systems with R-22 or blends.</li>
                <li><strong>Manufacturer's Warranty Items:</strong> Equipment under active manufacturer's warranty should be handled by the manufacturer. This contract is secondary to manufacturer warranties.</li>
              </ul>

              <h4>9. Contact Information</h4>
              <ul>
                <li><strong>Service & Scheduling:</strong> (561) 680-4467</li>
                <li><strong>Support & Cancellations:</strong> customer.support@my-ccs.com</li>
                <li><strong>Mailing Address:</strong> Climate Control Services, Inc., 1740 Corporate Drive, Boynton Beach, FL 33426</li>
              </ul>

              <h4>10. Acceptance</h4>
              <p>By enrolling in Standard, Premier, or Optimum Comfort Club, you acknowledge that you have read, understood, and accepted these Terms & Conditions, including all pricing, limits, and exclusions.</p>
            </div>
            <div className="p-6 border-t bg-gray-50">
              <button
                onClick={() => setShowTermsModal(false)}
                className="w-full py-3 px-4 rounded-xl font-semibold text-white"
                style={{ backgroundColor: primaryColor }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && proposal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-semibold">Accept Proposal</h2>
              <button onClick={closePaymentModal} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Amount Summary */}
              <div className="bg-emerald-50 rounded-xl p-4 text-center">
                <p className="text-sm text-emerald-600 font-medium">First Payment Due Today</p>
                <p className="text-3xl font-bold text-emerald-700">{formatCents(proposal.first_payment_cents || proposal.total_annual_price_cents)}</p>
              </div>

              {/* Signer Info */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Your Full Name *</label>
                  <input
                    type="text"
                    value={signerName}
                    onChange={(e) => setSignerName(e.target.value)}
                    placeholder="John Smith"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Your Title/Position</label>
                  <input
                    type="text"
                    value={signerTitle}
                    onChange={(e) => setSignerTitle(e.target.value)}
                    placeholder="Property Manager, Owner, etc."
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
              </div>

              {/* Terms Checkbox */}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="mt-1 w-5 h-5 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500"
                />
                <span className="text-sm text-gray-600">
                  I have read and agree to the{' '}
                  <button type="button" onClick={() => setShowTermsModal(true)} className="text-emerald-600 underline">
                    Comfort Club Terms & Conditions
                  </button>
                </span>
              </label>

              {/* Payment Method Selection */}
              {!paymentMethod && signerName.trim() && termsAccepted && (
                <div className="space-y-3">
                  <p className="font-medium text-gray-700">Select Payment Method:</p>
                  <button
                    onClick={() => setPaymentMethod('card')}
                    className="w-full flex items-center gap-4 p-4 border-2 border-gray-200 rounded-xl hover:border-emerald-500 transition-colors"
                  >
                    <CreditCard className="w-8 h-8 text-emerald-600" />
                    <div className="text-left">
                      <p className="font-semibold">Pay with Credit Card</p>
                      <p className="text-sm text-gray-500">Secure payment processed immediately</p>
                    </div>
                  </button>
                  <button
                    onClick={() => setPaymentMethod('check')}
                    className="w-full flex items-center gap-4 p-4 border-2 border-gray-200 rounded-xl hover:border-emerald-500 transition-colors"
                  >
                    <Landmark className="w-8 h-8 text-emerald-600" />
                    <div className="text-left">
                      <p className="font-semibold">Pay by Check</p>
                      <p className="text-sm text-gray-500">Mail check to our office</p>
                    </div>
                  </button>
                </div>
              )}

              {/* Credit Card Form */}
              {paymentMethod === 'card' && (
                <form ref={formRef} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name on Card</label>
                    <input
                      type="text"
                      name="card_name"
                      value={cardName}
                      onChange={(e) => setCardName(e.target.value)}
                      placeholder="Name as it appears on card"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Card Number</label>
                    <div id="card-number" className="w-full h-12 border border-gray-300 rounded-xl px-3 flex items-center bg-white" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Expiry (MM/YY)</label>
                      <input
                        type="text"
                        name="card_exp"
                        value={cardExpiry}
                        onChange={(e) => setCardExpiry(e.target.value)}
                        placeholder="MM/YY"
                        maxLength={5}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">CVV</label>
                      <div id="card-cvv" className="w-full h-12 border border-gray-300 rounded-xl px-3 flex items-center bg-white" />
                    </div>
                  </div>
                  {cardFormStatus === 'loading' && <p className="text-sm text-gray-500">Loading secure card form...</p>}
                  {cardFormStatus === 'error' && <p className="text-sm text-red-600">Failed to load secure card form. Please refresh.</p>}

                  <button
                    type="button"
                    onClick={handleAcceptWithCard}
                    disabled={paymentProcessing || cardFormStatus !== 'mounted' || !cardName}
                    className="w-full py-4 rounded-xl font-semibold text-white disabled:opacity-50"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {paymentProcessing ? 'Processing Payment...' : `Pay ${formatCents(proposal.first_payment_cents || proposal.total_annual_price_cents)}`}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setPaymentMethod(null); resetCardForm(); }}
                    className="w-full py-2 text-gray-600 hover:text-gray-800"
                  >
                    ← Back to payment options
                  </button>
                </form>
              )}

              {/* Check Payment */}
              {paymentMethod === 'check' && (
                <div className="space-y-4">
                  <div className="bg-blue-50 rounded-xl p-4">
                    <p className="font-semibold text-blue-800 mb-2">Check Payment Instructions</p>
                    <p className="text-sm text-blue-700 mb-3">Please make your check payable to:</p>
                    <div className="bg-white rounded-lg p-3 text-center">
                      <p className="font-bold">Climate Control Services, Inc.</p>
                      <p className="text-sm text-gray-600">1740 Corporate Drive</p>
                      <p className="text-sm text-gray-600">Boynton Beach, FL 33426</p>
                    </div>
                    <p className="text-sm text-blue-700 mt-3">
                      Amount: <strong>{formatCents(proposal.first_payment_cents || proposal.total_annual_price_cents)}</strong>
                    </p>
                    <p className="text-sm text-blue-700">
                      Reference: <strong>{proposal.customer_name}</strong>
                    </p>
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checkConfirmed}
                      onChange={(e) => setCheckConfirmed(e.target.checked)}
                      className="mt-1 w-5 h-5 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500"
                    />
                    <span className="text-sm text-gray-600">
                      I confirm I will mail a check for {formatCents(proposal.first_payment_cents || proposal.total_annual_price_cents)} within 5 business days.
                    </span>
                  </label>

                  <button
                    type="button"
                    onClick={handleAcceptWithCheck}
                    disabled={paymentProcessing || !checkConfirmed}
                    className="w-full py-4 rounded-xl font-semibold text-white disabled:opacity-50"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {paymentProcessing ? 'Processing...' : 'Accept Proposal & Pay by Check'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentMethod(null)}
                    className="w-full py-2 text-gray-600 hover:text-gray-800"
                  >
                    ← Back to payment options
                  </button>
                </div>
              )}

              {/* Error Message */}
              {paymentError && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <p className="text-red-700 text-sm">{paymentError}</p>
                </div>
              )}

              {/* Validation Message */}
              {(!signerName.trim() || !termsAccepted) && !paymentMethod && (
                <p className="text-sm text-gray-500 text-center">
                  Please enter your name and accept the terms to continue.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

